import { GetStaticProps } from "next";
import { useState } from "react";
import Head from "next/head";
import Image from "next/image";
import { useRouter } from "next/router";
import {
  findDefaultVariant,
  createSelectedOptionsFromVariant,
} from "../../../src/utils/shopifyHelpers";
import { ProductCard } from "../../../src/components/Shopify/ProductCard";
import { ShopButtons } from "@/src/components/Shopify";

interface ProductImage {
  id: string;
  url: string;
  altText: string;
  width: number;
  height: number;
}

interface ProductVariant {
  id: string;
  title: string;
  price: {
    amount: string;
    currencyCode: string;
  };
  availableForSale: boolean;
  selectedOptions: Array<{
    name: string;
    value: string;
  }>;
}

interface ProductOption {
  id: string;
  name: string;
  values: string[];
}

interface Product {
  id: string;
  title: string;
  handle: string;
  description: string;
  descriptionHtml: string;
  images: {
    edges: Array<{
      node: ProductImage;
    }>;
  };
  variants: {
    edges: Array<{
      node: ProductVariant;
    }>;
  };
  options: ProductOption[];
  priceRange: {
    minVariantPrice: {
      amount: string;
      currencyCode: string;
    };
    maxVariantPrice: {
      amount: string;
      currencyCode: string;
    };
  };
  availableForSale: boolean;
  tags: string[];
  vendor: string;
  collections: {
    edges: Array<{
      node: {
        id: string;
        handle: string;
        title: string;
      };
    }>;
  };
}

interface RelatedProduct {
  id: string;
  title: string;
  handle: string;
  description: string;
  images: {
    edges: Array<{
      node: {
        id: string;
        url: string;
        altText: string;
      };
    }>;
  };
  priceRange: {
    minVariantPrice: {
      amount: string;
      currencyCode: string;
    };
  };
  variants: {
    edges: Array<{
      node: {
        id: string;
        availableForSale: boolean;
      };
    }>;
  };
}

interface ProductPageProps {
  product: Product;
  relatedProducts: RelatedProduct[];
}

const PRODUCT_QUERY = `
  query Product($handle: String!) {
    product(handle: $handle) {
      id
      title
      handle
      description
      descriptionHtml
      availableForSale
      tags
      vendor
      collections(first: 5) {
        edges {
          node {
            id
            handle
            title
          }
        }
      }
      images(first: 10) {
        edges {
          node {
            id
            url
            altText
            width
            height
          }
        }
      }
      variants(first: 20) {
        edges {
          node {
            id
            title
            price {
              amount
              currencyCode
            }
            availableForSale
            selectedOptions {
              name
              value
            }
          }
        }
      }
      options {
        id
        name
        values
      }
      priceRange {
        minVariantPrice {
          amount
          currencyCode
        }
        maxVariantPrice {
          amount
          currencyCode
        }
      }
    }
  }
`;

const RELATED_PRODUCTS_QUERY = `
  query RelatedProducts($collectionHandle: String!) {
    collection(handle: $collectionHandle) {
      products(first: 8) {
        edges {
          node {
            id
            title
            handle
            description
            images(first: 1) {
              edges {
                node {
                  id
                  url
                  altText
                }
              }
            }
            priceRange {
              minVariantPrice {
                amount
                currencyCode
              }
            }
            variants(first: 1) {
              edges {
                node {
                  id
                  availableForSale
                }
              }
            }
          }
        }
      }
    }
  }
`;

// RelatedProducts Component
interface RelatedProductsProps {
  products: RelatedProduct[];
  collectionTitle?: string;
}

const RelatedProducts = ({
  products,
  collectionTitle,
}: RelatedProductsProps) => {
  if (!products || products.length === 0) {
    return null;
  }

  return (
    <div className="bg-gray-50 py-8">
      <div className="md:container mx-auto px-4">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Related</h2>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {products.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default function ProductPage({
  product,
  relatedProducts,
}: ProductPageProps) {
  const router = useRouter();

  const variants = product.variants.edges.map((edge) => edge.node);
  const defaultVariant = findDefaultVariant(variants);
  const defaultSelectedOptions =
    createSelectedOptionsFromVariant(defaultVariant);

  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(
    defaultVariant
  );
  const [selectedOptions, setSelectedOptions] = useState<
    Record<string, string>
  >(defaultSelectedOptions);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [addToCartSuccess, setAddToCartSuccess] = useState(false);
  const [quantity, setQuantity] = useState(1);

  const images = product.images.edges.map((edge) => edge.node);

  // Check if an option value is available given current selections
  const isOptionValueAvailable = (optionName: string, optionValue: string) => {
    // Create a hypothetical selection with this option value
    const hypotheticalSelection = {
      ...selectedOptions,
      [optionName]: optionValue,
    };

    // Check if any variant matches this selection and is available
    return variants.some((variant) => {
      if (!variant.availableForSale) return false;

      // Check if this variant is compatible with all hypothetical selections
      return Object.entries(hypotheticalSelection).every(([name, value]) => {
        // Find if this variant has this option with this value
        return variant.selectedOptions.some(
          (selectedOption) =>
            selectedOption.name === name && selectedOption.value === value
        );
      });
    });
  };

  // Handle option changes
  const handleOptionChange = (optionName: string, value: string) => {
    const newOptions = { ...selectedOptions, [optionName]: value };
    setSelectedOptions(newOptions);

    // Find the variant that matches the selected options
    const variant = variants.find((v) =>
      v.selectedOptions.every(
        (option) => newOptions[option.name] === option.value
      )
    );

    if (variant) {
      setSelectedVariant(variant);
    }
  };

  // Handle add to cart success
  const handleAddToCartSuccess = () => {
    setAddToCartSuccess(true);

    // Hide success message after 3 seconds
    setTimeout(() => {
      setAddToCartSuccess(false);
    }, 3000);
  };

  const formatPrice = (price: { amount: string; currencyCode: string }) => {
    return `$${price.amount}`;
  };

  const primaryCollection = product.collections?.edges[0]?.node;

  return (
    <>
      <Head>
        <title>{product.title} - SC Playbook Store</title>
        <meta name="description" content={product.description} />
        <link
          rel="canonical"
          href={`https://scplaybook.com.au/shop/product/${product.handle}`}
        />
      </Head>

      <div className="min-h-screen bg-gray-50">
        <div className="md:container mx-auto px-2 sm:px-4 py-4 sm:py-8">
          {/* Back to shop with Cart Info */}
          <div className="flex justify-between items-center mb-4 sm:mb-6">
            <button
              onClick={() => router.push("/shop")}
              className="text-scpb-light-blue hover:text-blue-800 flex items-center gap-2"
            >
              <span>←</span> Back to Shop
            </button>
          </div>

          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 p-3 sm:p-0">
              {/* Product Images */}
              <div className="lg:container lg:mx-auto ">
                <div className="">
                  <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                    {images.length > 0 ? (
                      <Image
                        src={images[selectedImageIndex].url}
                        alt={
                          images[selectedImageIndex].altText || product.title
                        }
                        width={images[selectedImageIndex].width}
                        height={images[selectedImageIndex].height}
                        className="w-full h-full object-cover"
                        priority
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        <span>No Image Available</span>
                      </div>
                    )}
                  </div>

                  {/* Image thumbnails */}
                  {images.length > 1 && (
                    <div className="flex gap-1 sm:gap-2 overflow-x-auto p-2">
                      {images.map((image, index) => (
                        <button
                          key={image.id}
                          onClick={() => setSelectedImageIndex(index)}
                          className={`flex-shrink-0 w-12 h-12 sm:w-16 sm:h-16 rounded-md overflow-hidden border-2 ${
                            selectedImageIndex === index
                              ? "border-scpb-light-blue"
                              : "border-gray-200"
                          }`}
                        >
                          <Image
                            src={image.url}
                            alt={
                              image.altText || `${product.title} ${index + 1}`
                            }
                            width={64}
                            height={64}
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Product Details */}
              <div className="space-y-4 sm:space-y-6 p-2">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">
                    {product.title}
                  </h1>
                  <div className="text-2xl font-bold text-scpb-light-blue">
                    {selectedVariant
                      ? formatPrice(selectedVariant.price)
                      : formatPrice(product.priceRange.minVariantPrice)}
                  </div>
                </div>

                {/* Product Description */}
                <div>
                  <div
                    className="text-gray-700 prose max-w-none product-description"
                    dangerouslySetInnerHTML={{
                      __html: product.descriptionHtml,
                    }}
                  />
                </div>

                {/* Product Options */}
                {product.options.map((option) => (
                  <div key={option.id}>
                    <h4 className="font-semibold mb-2">{option.name}</h4>
                    <div className="flex flex-wrap gap-2">
                      {option.values.map((value) => {
                        const isAvailable = isOptionValueAvailable(
                          option.name,
                          value
                        );
                        return (
                          <button
                            key={value}
                            onClick={() =>
                              isAvailable &&
                              handleOptionChange(option.name, value)
                            }
                            disabled={!isAvailable}
                            className={`px-4 py-2 rounded-md border transition-colors ${
                              selectedOptions[option.name] === value
                                ? "border-scpb-light-blue bg-scpb-light-blue text-white"
                                : !isAvailable
                                ? "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed opacity-50"
                                : "border-gray-300 hover:border-gray-400 cursor-pointer"
                            }`}
                          >
                            {value}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}

                {/* Quantity Selector */}
                <div>
                  <h4 className="font-semibold mb-2">Quantity</h4>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      disabled={quantity <= 1}
                      className="w-10 h-10 rounded-md border border-gray-300 flex items-center justify-center hover:border-gray-400 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                    >
                      −
                    </button>
                    <input
                      type="number"
                      value={quantity}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 1;
                        setQuantity(Math.max(1, Math.min(99, value)));
                      }}
                      min="1"
                      max="99"
                      className="w-16 h-10 text-center border border-gray-300 rounded-md focus:ring-2 focus:ring-scpb-light-blue focus:border-scpb-light-blue"
                    />
                    <button
                      onClick={() => setQuantity(Math.min(99, quantity + 1))}
                      disabled={quantity >= 99}
                      className="w-10 h-10 rounded-md border border-gray-300 flex items-center justify-center hover:border-gray-400 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                    >
                      +
                    </button>
                  </div>
                </div>

                {/* Shop Buttons (Add to Cart + Shop Pay) */}
                <ShopButtons
                  selectedVariant={selectedVariant}
                  quantity={quantity}
                  onAddToCartSuccess={handleAddToCartSuccess}
                />

                {/* Product Tags */}
                {product.tags.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-2">Tags</h4>
                    <div className="flex flex-wrap gap-2">
                      {product.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Related Products Section */}
        <RelatedProducts
          products={relatedProducts}
          collectionTitle={primaryCollection?.title}
        />
      </div>
    </>
  );
}

const ALL_PRODUCTS_QUERY = `
  query AllProducts($first: Int!, $after: String) {
    products(first: $first, after: $after) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          handle
        }
      }
    }
  }
`;

export async function getStaticPaths() {
  const allProducts = [];
  let hasNextPage = true;
  let cursor = null;

  try {
    // Fetch all products with pagination
    while (hasNextPage) {
      // TODO shopifyenv
      const response = await fetch(
        `https://heganx-1j.myshopify.com/api/2023-01/graphql.json`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Shopify-Storefront-Access-Token":
              "516df6029e81195e555c1e067839d180",
          },
          body: JSON.stringify({
            query: ALL_PRODUCTS_QUERY,
            variables: {
              first: 250, // Shopify's max per request
              after: cursor,
            },
          }),
        }
      );

      const data = await response.json();
      const products = data.data?.products;

      if (products?.edges) {
        allProducts.push(...products.edges);
      }

      hasNextPage = products?.pageInfo?.hasNextPage || false;
      cursor = products?.pageInfo?.endCursor || null;
    }

    // Generate paths for all products
    const paths = allProducts.map((product) => ({
      params: { slug: [product.node.handle] },
    }));

    return {
      paths,
      fallback: "blocking", // Enable ISR for new products
    };
  } catch (error) {
    console.error("Error fetching products for static paths:", error);
    return {
      paths: [],
      fallback: "blocking",
    };
  }
}

export const getStaticProps: GetStaticProps = async (context) => {
  const { slug } = context.params!;
  const handle = Array.isArray(slug) ? slug[0] : slug;

  if (!handle) {
    return {
      notFound: true,
    };
  }

  try {
    // TODO shopifyenv
    const response = await fetch(
      `https://heganx-1j.myshopify.com/api/2023-01/graphql.json`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token":
            "516df6029e81195e555c1e067839d180",
        },
        body: JSON.stringify({
          query: PRODUCT_QUERY,
          variables: { handle },
        }),
      }
    );

    const data = await response.json();
    const product = data.data?.product;

    if (!product) {
      return {
        notFound: true,
      };
    }

    // Fetch related products from the first collection
    let relatedProducts: RelatedProduct[] = [];

    const primaryCollection = product.collections.edges[0]?.node;

    if (primaryCollection) {
      try {
        // TODO shopifyenv
        const relatedResponse = await fetch(
          `https://heganx-1j.myshopify.com/api/2023-01/graphql.json`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Shopify-Storefront-Access-Token":
                "516df6029e81195e555c1e067839d180",
            },
            body: JSON.stringify({
              query: RELATED_PRODUCTS_QUERY,
              variables: {
                collectionHandle: primaryCollection.handle,
              },
            }),
          }
        );

        const relatedData = await relatedResponse.json();
        relatedProducts =
          relatedData.data?.collection?.products?.edges
            ?.map((edge: any) => edge.node)
            .filter(
              (relatedProduct: any) => relatedProduct.id !== product.id
            ) || [];
      } catch (error) {
        console.error("Error fetching related products:", error);
      }
    }

    return {
      props: {
        product,
        relatedProducts,
      },
      revalidate: 120, // Revalidate every 2 minutes for ISR
    };
  } catch (error) {
    console.error("Error fetching product:", error);
    return {
      notFound: true,
    };
  }
};
